import type { LayoutListActionNode } from "../../types.js";
import {
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  PatchResult,
  RuntimeSignal,
} from "../types.js";
import {
  emptyPatchResult,
  ensureBindingExpression,
  toFiniteNumber,
} from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class CreateRowVariantBehaviorNode extends BehaviorNode {
  declare behavior: LayoutListActionNode;

  constructor(behavior: LayoutListActionNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const patch: PatchResult = emptyPatchResult();
    const visiting = new Set<string>();
    const name = String(
      this.getInput("name", visiting, context, false) ?? "",
    ).trim();
    if (!name) {
      return this.finish(patch, context);
    }

    const order = toFiniteNumber(this.getInput("order", visiting, context), 0);
    const conditionExpression = ensureBindingExpression(
      this.getInput("condition", visiting, context, true),
    );
    if (!conditionExpression) {
      return this.finish(patch, context);
    }

    patch.componentBindingPatch = {
      rowVariantBindingRules: [
        {
          name,
          order,
          conditionExpression,
        },
      ],
    };

    return this.finish(patch, context);
  }

  getOutput(
    _portId: string,
    _visiting: Set<string>,
    _context: ExecutionContext,
  ): unknown {
    return undefined;
  }

  private finish(
    patch: PatchResult,
    context: ExecutionContext,
  ): NodeExecutionResult {
    const nextFlows = this.getNextFlows(context);
    const runtimeSignals: RuntimeSignal[] = [];
    if (nextFlows.length === 0 && context.loopStack.length > 0) {
      runtimeSignals.push({ type: "loop_continue", context });
    }
    return {
      patch,
      nextFlows,
      scheduledNodes: [],
      runtimeSignals,
    };
  }

  private getNextFlows(context: ExecutionContext): BehaviorFlow[] {
    return (this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
      edge,
      context,
    }));
  }
}
